import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { Box, Divider, Drawer, IconButton, List, Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';

import { useIsDesktop, useIsMobile } from '../hooks/useBreakpoint';
import type { NavLeaf, NavNode } from '../types/nav.types';

import { NavItem } from './NavItem';
import { SideNav } from './SideNav';
import type { UserMenuAction } from './UserMenu';
import { UserMenu } from './UserMenu';

const EXPANDED_WIDTH = 234;
const COLLAPSED_WIDTH = 72;

interface AppShellProps {
  nav: NavNode[];
  // A page pinned to the bottom of the rail, directly above the user section, instead of
  // scrolling with the main tree (e.g. "View all notifications"). Same NavLeaf shape and the
  // same activePath/onNavigate wiring as `nav` — it's just rendered outside the scroll area,
  // plus an optional unread-style count badge the scrolling tree has no use for.
  footerNav?: NavLeaf & { badgeCount?: number };
  activePath: string;
  onNavigate: (path: string) => void;
  productIcon: ReactNode;
  productName: string;
  productVersion: string;
  userName: string;
  userEmail: string;
  userAvatarUrl?: string;
  userRoleLabel: string;
  userActions: UserMenuAction[];
  children: ReactNode;
}

// Owns the responsive Drawer + collapse state. Below `md`, the rail collapses by default;
// below `sm`, a manual "expand" renders as a temporary overlay instead of pushing content
// (matches MUI's own canonical responsive-drawer pattern, avoids squeezing phone-width
// content). From the user's perspective there are still just two visual states — expanded
// or icon-only — this is purely an internal detail of how "expanded" is presented per size.
export function AppShell({
  nav,
  footerNav,
  activePath,
  onNavigate,
  productIcon,
  productName,
  productVersion,
  userName,
  userEmail,
  userAvatarUrl,
  userRoleLabel,
  userActions,
  children,
}: AppShellProps) {
  const isDesktop = useIsDesktop();
  const isMobile = useIsMobile();
  const [collapsedOverride, setCollapsedOverride] = useState<boolean | null>(null);

  // Drop any manual override when the breakpoint category itself changes, so resizing the
  // window doesn't leave the user stuck with a stale preference from a different size class.
  useEffect(() => {
    setCollapsedOverride(null);
  }, [isDesktop, isMobile]);

  const collapsed = collapsedOverride ?? !isDesktop;
  const showAsTemporary = isMobile && !collapsed;
  const toggle = () => setCollapsedOverride(!collapsed);

  const headerExpanded = !collapsed || showAsTemporary;

  const drawerContent = (
    <Stack sx={{ height: '100%' }}>
      <Stack alignItems="center" sx={{ py: 2 }}>
        {headerExpanded ? (
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            justifyContent="space-between"
            sx={{ px: 2, width: '100%' }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ overflow: 'hidden' }}>
              {productIcon}
              <Box sx={{ overflow: 'hidden' }}>
                <Typography variant="subtitle1" fontWeight={700} noWrap>
                  {productName}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {productVersion}
                </Typography>
              </Box>
            </Stack>
            <IconButton size="small" onClick={toggle} aria-label="Collapse navigation">
              <ChevronLeft fontSize="small" />
            </IconButton>
          </Stack>
        ) : (
          <Stack spacing={1} alignItems="center">
            {productIcon}
            <IconButton size="small" onClick={toggle} aria-label="Expand navigation">
              <ChevronRight fontSize="small" />
            </IconButton>
          </Stack>
        )}
      </Stack>
      <Divider />
      <Box sx={{ flexGrow: 1, overflowY: 'auto', py: 1 }}>
        <SideNav
          nav={nav}
          activePath={activePath}
          onNavigate={onNavigate}
          collapsed={collapsed && !showAsTemporary}
        />
      </Box>
      <Divider />
      {footerNav && (
        <>
          <List disablePadding>

            <NavItem
              icon={footerNav.icon}
              label={footerNav.label}
              active={activePath === footerNav.path}
              collapsed={collapsed && !showAsTemporary}
              badgeCount={footerNav.badgeCount}
              onClick={() => onNavigate(footerNav.path)}
            />
          </List>
          <Divider />
        </>
      )}
      <UserMenu
        name={userName}
        email={userEmail}
        avatarUrl={userAvatarUrl}
        roleLabel={userRoleLabel}
        actions={userActions}
        collapsed={collapsed && !showAsTemporary}
      />
    </Stack>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {showAsTemporary ? (
        <Drawer
          variant="temporary"
          open
          onClose={toggle}
          ModalProps={{ keepMounted: true }}
          sx={{ '& .MuiDrawer-paper': { width: EXPANDED_WIDTH, boxSizing: 'border-box' } }}
        >
          {drawerContent}
        </Drawer>
      ) : (
        <Drawer
          variant="permanent"
          sx={{
            width: collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH,
              boxSizing: 'border-box',
              transition: 'width 0.2s',
              overflowX: 'hidden',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}
      <Box component="main" sx={{ flexGrow: 1, minWidth: 0 }}>
        {children}
      </Box>
    </Box>
  );
}
